import React, { createContext, useContext } from 'react';
import toast, { Toaster } from 'react-hot-toast';
import { colors } from '../styles/designTokens';

/**
 * Notification Context
 * 
 * Provides toast notification functionality throughout the app.
 * Replaces window.alert(), window.confirm(), and window.prompt()
 * 
 * Usage:
 * const { showSuccess, showError, showWarning, showInfo } = useNotification();
 * showSuccess('Lead saved successfully!');
 * showError('Connection lost. Please check your internet.');
 */

const NotificationContext = createContext(null);

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  // Success notification (green)
  const showSuccess = (message, options = {}) => {
    toast.success(message, {
      duration: options.duration || 4000,
      position: options.position || 'top-right',
      icon: '✅',
      style: {
        background: colors.success[50],
        color: colors.success[700],
        border: `1px solid ${colors.success[200]}`,
        borderRadius: '0.5rem',
        padding: '1rem',
        fontSize: '0.875rem',
        fontWeight: 500,
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
      },
      ...options
    });
  };

  // Error notification (red)
  const showError = (message, options = {}) => {
    toast.error(message, {
      duration: options.duration || 5000, // Errors stay longer
      position: options.position || 'top-right',
      icon: '❌',
      style: {
        background: colors.error[50],
        color: colors.error[700],
        border: `1px solid ${colors.error[200]}`,
        borderRadius: '0.5rem',
        padding: '1rem',
        fontSize: '0.875rem',
        fontWeight: 500,
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
      },
      ...options
    });
  };

  // Warning notification (yellow)
  const showWarning = (message, options = {}) => {
    toast(message, {
      duration: options.duration || 4500,
      position: options.position || 'top-right',
      icon: '⚠️',
      style: {
        background: colors.warning[50],
        color: colors.warning[700],
        border: `1px solid ${colors.warning[200]}`,
        borderRadius: '0.5rem',
        padding: '1rem',
        fontSize: '0.875rem',
        fontWeight: 500,
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
      },
      ...options
    });
  };

  // Info notification (blue)
  const showInfo = (message, options = {}) => {
    toast(message, {
      duration: options.duration || 4000,
      position: options.position || 'top-right',
      icon: 'ℹ️',
      style: {
        background: colors.info[50],
        color: colors.info[700],
        border: `1px solid ${colors.info[200]}`,
        borderRadius: '0.5rem',
        padding: '1rem',
        fontSize: '0.875rem',
        fontWeight: 500,
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
      },
      ...options
    });
  };

  // Custom notification with full control
  const showCustom = (message, options = {}) => {
    toast(message, {
      duration: options.duration || 4000,
      position: options.position || 'top-right',
      ...options
    });
  };

  // Promise-based notification (for async operations)
  const showPromise = (promise, messages) => {
    return toast.promise(
      promise,
      {
        loading: messages.loading || 'Loading...',
        success: messages.success || 'Success!',
        error: messages.error || 'Error occurred',
      },
      {
        position: 'top-right',
        style: {
          borderRadius: '0.5rem',
          padding: '1rem',
          fontSize: '0.875rem',
          fontWeight: 500,
        },
        success: {
          icon: '✅',
          style: {
            background: colors.success[50],
            color: colors.success[700],
            border: `1px solid ${colors.success[200]}`,
          },
        },
        error: {
          icon: '❌',
          style: {
            background: colors.error[50],
            color: colors.error[700],
            border: `1px solid ${colors.error[200]}`,
          },
        },
      }
    );
  };

  // Dismiss all toasts
  const dismissAll = () => {
    toast.dismiss();
  };

  // Dismiss specific toast
  const dismiss = (toastId) => {
    toast.dismiss(toastId);
  };

  const value = {
    showSuccess,
    showError,
    showWarning,
    showInfo,
    showCustom,
    showPromise,
    dismiss,
    dismissAll
  };

  return (
    <NotificationContext.Provider value={value}>
      {/* Toaster component renders the toast container */}
      <Toaster
        position="top-right"
        reverseOrder={false}
        gutter={8}
        containerStyle={{
          top: 20,
          right: 20,
        }}
        toastOptions={{
          // Default options for all toasts
          duration: 4000,
          style: {
            background: '#fff',
            color: '#374151',
            borderRadius: '0.5rem',
            padding: '1rem',
            fontSize: '0.875rem',
            fontWeight: 500,
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            maxWidth: '500px'
          },
          // Animation settings
          success: {
            duration: 4000,
            iconTheme: {
              primary: colors.success.main,
              secondary: '#fff',
            },
          },
          error: {
            duration: 5000,
            iconTheme: {
              primary: colors.error.main,
              secondary: '#fff',
            },
          },
        }}
      />
      {children}
    </NotificationContext.Provider>
  );
};

export default NotificationContext;

